/**
 * Event details (DetailPanel, DESIGN.md T2): time, recurrence, location, organizer, attendees with their answers,
 * Accept / Tentative / Decline for invitations, Edit and Delete (cancel for organizers). Free/busy blocks and
 * private events of others show only what the server returned (masked).
 */
import { useI18n } from '@platform/i18n';
import {
  Badge,
  Button,
  ConfirmDialog,
  DescriptionList,
  DetailPanel,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  MailHtmlViewer,
  PersonAvatar,
  Section,
  Skeleton,
  sanitizeMailHtml,
  useAsync,
  useErrorText,
} from '@platform/ui';
import { CheckIcon, CircleHelpIcon, ClockIcon, CrownIcon, HelpCircleIcon, PencilIcon, Trash2Icon, VideoIcon, XIcon } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { api, type CalendarEvent, type CalendarEventScope } from '../api';
import { useCalendar, type EditScope } from './calendar-context';
import { calendarName, isBlock, RESPONSE_LABEL, SHOW_AS_LABEL, type ShownEvent } from './events';
import { useCalendarFormat } from './format';
import { recurrenceText } from './recurrence';
import { RsvpButtons } from './rsvp';
import { EventAttachmentList } from './editor-attachments';
import { useUndoableCalendar } from './undo';

const RESPONSE_ICON: Record<CalendarEvent['myResponse'], ReactNode> = {
  accepted: <CheckIcon className="size-3.5 text-success" />,
  declined: <XIcon className="size-3.5 text-destructive" />,
  tentative: <HelpCircleIcon className="size-3.5 text-warning" />,
  organizer: <CrownIcon className="size-3.5 text-muted-foreground" />,
  none: <ClockIcon className="size-3.5 text-muted-foreground" />,
  unknown: <CircleHelpIcon className="size-3.5 text-muted-foreground" />,
};

export interface EventPanelProps {
  item: ShownEvent;
  onClose: () => void;
  /** After a change (answer, delete): the grid reloads. */
  onChanged: () => void;
  /** The "…" menu with the event actions (same registry as the right-click menu). */
  menu?: ReactNode;
}

export function EventPanel({ item, onClose, onChanged, menu }: EventPanelProps) {
  const { t } = useI18n();
  const format = useCalendarFormat();
  const errorText = useErrorText();
  const { openEditor, zone } = useCalendar();
  const { event, calendar } = item;
  const canOpen = !isBlock(event) && !event.masked;
  const detail = useAsync(() => (canOpen ? api.getEvent(event.id) : Promise.resolve(undefined)), event.id);
  const [confirm, setConfirm] = useState<CalendarEventScope>();
  const undoable = useUndoableCalendar();
  const full = detail.data ?? event;
  const start = zone.toWall(full.start);
  const end = zone.toWall(full.end);
  const invite = full.isMeeting && !full.isOrganizer;
  const canEdit = !full.readOnly && (full.isOrganizer || !full.isMeeting);
  const title = event.masked ? (event.sensitivity === 'private' ? t('webmail.calendar.privateEvent') : t(SHOW_AS_LABEL[event.showAs])) : full.subject || t('webmail.calendar.noTitle');

  const edit = async (scope: EditScope) => {
    try {
      const loaded = detail.data ?? (await api.getEvent(event.id));
      openEditor({ mode: 'edit', event: loaded, scope });
    } catch (error) {
      errorText.notify(error);
    }
  };

  const remove = () => {
    if (!confirm) return;
    // Meetings confirm first (the attendees are told); the delete waits for the Undo time (undo.ts).
    undoable.remove(event, { scope: confirm, title });
    onChanged();
  };
  const askRemove = (scope: EditScope) => {
    if (scope === 'following') return; // not offered for delete
    if (full.isMeeting) setConfirm(scope);
    else {
      undoable.remove(event, { scope, title });
      onChanged();
    }
  };

  const scopeButtons = (label: string, icon: ReactNode, onSelect: (scope: EditScope) => void, variant: 'outline' | 'destructive' = 'outline', following = false) =>
    full.isRecurring ? (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size="sm">
            {icon}
            {label}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onSelect={() => onSelect('occurrence')}>{t('webmail.calendar.scope.occurrence')}</DropdownMenuItem>
          {following && <DropdownMenuItem onSelect={() => onSelect('following')}>{t('webmail.calendarUx.recurrence.following')}</DropdownMenuItem>}
          <DropdownMenuItem onSelect={() => onSelect('series')}>{t('webmail.calendar.scope.series')}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <Button variant={variant} size="sm" onClick={() => onSelect('occurrence')}>
        {icon}
        {label}
      </Button>
    );

  const actions = (
    <>
      {full.onlineMeetingUrl && (
        <Button size="sm" asChild>
          <a href={full.onlineMeetingUrl} target="_blank" rel="noopener noreferrer">
            <VideoIcon />
            {t('webmail.calendarUx.join')}
          </a>
        </Button>
      )}
      {invite && !full.readOnly && <RsvpButtons event={full} onDone={onChanged} />}
      {canEdit && scopeButtons(t('webmail.calendar.edit'), <PencilIcon />, (scope) => void edit(scope), 'outline', true)}
      {full.canDelete && scopeButtons(t(full.isMeeting && full.isOrganizer ? 'webmail.calendar.cancelMeeting' : 'webmail.calendar.delete'), <Trash2Icon />, askRemove, 'destructive')}
      {menu}
    </>
  );

  const people = full.attendees.filter((a) => a.type !== 'resource');
  const resources = full.attendees.filter((a) => a.type === 'resource');
  const html = detail.data?.bodyHtml ? sanitizeMailHtml(detail.data.bodyHtml, { allowRemote: false }) : undefined;

  return (
    <DetailPanel phoneSheet="bottom" title={title} subtitle={format.range(start, end, full.allDay)} onClose={onClose} actions={actions} status={<Badge variant="muted">{calendarName(calendar, t)}</Badge>}>
      {detail.loading && canOpen && !detail.data ? (
        <div className="flex flex-col gap-2 py-3" aria-busy="true">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
      ) : (
        <>
          <Section title={t('webmail.calendar.details')}>
            <DescriptionList
              items={[
                ...(full.recurrence ? [{ label: t('webmail.calendar.repeats'), value: recurrenceText(full.recurrence, t, format) }] : []),
                ...(full.location ? [{ label: t('webmail.calendar.location'), value: full.location }] : []),
                ...(full.categories.length > 0 ? [{ label: t('webmail.calendarUx.editor.categories'), value: format.list(full.categories) }] : []),
                ...(resources.length > 0 ? [{ label: t('webmail.calendar.rooms'), value: format.list(resources.map((r) => r.name)) }] : []),
                { label: t('webmail.calendar.showAs.label'), value: t(SHOW_AS_LABEL[full.showAs]) },
                ...(full.isMeeting ? [{ label: t('webmail.calendar.myResponse'), value: t(RESPONSE_LABEL[full.myResponse]) }] : []),
                ...(full.sensitivity === 'private' ? [{ label: t('webmail.calendar.privacy'), value: t('webmail.calendar.private') }] : []),
                ...(full.reminderMinutes !== null ? [{ label: t('webmail.calendar.reminder'), value: t('webmail.calendar.reminderMinutes', { n: full.reminderMinutes }) }] : []),
              ]}
            />
            {event.masked && <p className="text-xs text-muted-foreground">{t(event.sensitivity === 'private' ? 'webmail.calendar.maskedPrivate' : 'webmail.calendar.maskedBusy')}</p>}
          </Section>
          {full.organizer && (
            <Section title={t('webmail.calendar.organizer')}>
              <div className="flex items-center gap-2 text-sm">
                <PersonAvatar name={full.organizer.name} email={full.organizer.address} />
                <span className="truncate">{full.organizer.name}</span>
              </div>
            </Section>
          )}
          {people.length > 0 && (
            <Section title={t('webmail.calendar.attendeesCount', { n: people.length })}>
              <ul className="flex flex-col gap-1.5">
                {people.map((a) => (
                  <li key={a.address} className="flex items-center gap-2 text-sm">
                    <PersonAvatar name={a.name} email={a.address} />
                    <span className="min-w-0 flex-1 truncate" title={a.address}>
                      {a.name}
                      {a.type === 'optional' && (
                        <Badge variant="outline" className="ml-1">
                          {t('webmail.calendar.optional')}
                        </Badge>
                      )}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground" title={t(RESPONSE_LABEL[a.response])}>
                      {RESPONSE_ICON[a.response]}
                      <span className="sr-only">{t(RESPONSE_LABEL[a.response])}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {full.hasAttachments && canOpen && (
            <Section title={t('webmail.calendarUx.editor.attachments')}>
              <EventAttachmentList eventId={event.id} />
            </Section>
          )}
          {html && html.html.trim() !== '' && (
            <Section title={t('webmail.calendar.description')}>
              <div className="h-64">
                <MailHtmlViewer html={html.html} allowRemote={false} ownColors={html.ownColors} title={t('webmail.calendar.description')} />
              </div>
            </Section>
          )}
        </>
      )}
      <ConfirmDialog
        open={confirm !== undefined}
        onOpenChange={(open) => !open && setConfirm(undefined)}
        title={t(full.isMeeting && full.isOrganizer ? 'webmail.calendar.cancelTitle' : 'webmail.calendar.deleteTitle')}
        description={t(full.isMeeting && full.isOrganizer ? 'webmail.calendar.cancelDescription' : full.isMeeting ? 'webmail.calendar.declineDeleteDescription' : 'webmail.calendar.deleteDescription', { title })}
        confirmLabel={t(full.isMeeting && full.isOrganizer ? 'webmail.calendar.cancelMeeting' : 'webmail.calendar.delete')}
        onConfirm={remove}
      />
    </DetailPanel>
  );
}
